import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepOutlined from '@mui/icons-material/DeleteSweepOutlined';
import SendIcon from '@mui/icons-material/Send';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import QuestionAnswerOutlined from '@mui/icons-material/QuestionAnswerOutlined';
import { Notice } from '@routes-plan/ui';
import { currentLang, useT } from '../../i18n';
import { useScreenHelp } from '../help/ScreenHelp';

/**
 * Chat de ayuda con IA (`POST help/ask`): responde solo con las guías y propone botones que llevan a la
 * pantalla correcta. No consulta datos de la empresa. La conversación vive solo en memoria.
 *
 * Se abre con el botón flotante o desde cualquier parte con `openHelpChat()` (por ejemplo, la opción
 * "Preguntar" del menú de ayuda de la barra superior). Si el API dice que el chat está apagado
 * (`HELP_CHAT_DISABLED`, sin clave de Anthropic) no se muestra nada.
 */

const OPEN_EVENT = 'ruttia:help-chat-open';

/** Abre el chat de ayuda (desde el menú de ayuda u otra pantalla). */
export const openHelpChat = () => window.dispatchEvent(new Event(OPEN_EVENT));

/** Si el chat está disponible (el API tiene clave): lo usa el menú de ayuda para mostrar "Preguntar". */
const STATUS_EVENT = 'ruttia:help-chat-status';
let chatEnabled = false;
const publishEnabled = (enabled: boolean) => {
  if (chatEnabled === enabled) return;
  chatEnabled = enabled;
  window.dispatchEvent(new Event(STATUS_EVENT));
};
export const useHelpChatEnabled = () => {
  const [enabled, setEnabled] = React.useState(chatEnabled);
  React.useEffect(() => {
    const sync = () => setEnabled(chatEnabled);
    window.addEventListener(STATUS_EVENT, sync);
    sync();
    return () => window.removeEventListener(STATUS_EVENT, sync);
  }, []);
  return enabled;
};

interface ChatLink {
  label: string;
  path: string;
}

interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  links?: ChatLink[];
  /** Mensaje de error del chat: no se manda como historial. */
  error?: 'warning' | 'error';
}

interface ChatStatus {
  enabled: boolean;
  maxQuestionLength?: number;
  remaining?: number | null;
}

/** Turnos previos que se mandan como contexto (el API recorta otra vez). */
const HISTORY_SENT = 12;

const blink = keyframes`
  0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-2px); }
`;

/** Negrita `**texto**` sin HTML. */
const inline = (text: string) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4 ? <strong key={i}>{part.slice(2, -2)}</strong> : <React.Fragment key={i}>{part}</React.Fragment>,
  );

/** Respuesta del asistente: párrafos y listas numeradas o con viñetas (Markdown mínimo, sin HTML). */
const AnswerText: React.FC<{ text: string }> = ({ text }) => {
  const blocks: React.ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  const flush = () => {
    if (!list) return;
    const Tag = list.ordered ? 'ol' : 'ul';
    blocks.push(
      <Box key={blocks.length} component={Tag} sx={{ my: 0.5, pl: 2.5, '& li': { mb: 0.5 } }}>
        {list.items.map((item, i) => (
          <li key={i}>{inline(item)}</li>
        ))}
      </Box>,
    );
    list = null;
  };
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    const ordered = line.match(/^\d+[.)]\s+(.*)$/);
    const bullet = line.match(/^[-*•]\s+(.*)$/);
    if (ordered || bullet) {
      const isOrdered = !!ordered;
      if (list && list.ordered !== isOrdered) flush();
      if (!list) list = { ordered: isOrdered, items: [] };
      list.items.push((ordered || bullet)![1]);
      continue;
    }
    flush();
    if (line) {
      blocks.push(
        <Typography key={blocks.length} variant="body2" sx={{ my: 0.5 }}>
          {inline(line.replace(/^#+\s*/, ''))}
        </Typography>,
      );
    }
  }
  flush();
  return <Box sx={{ typography: 'body2', overflowWrap: 'anywhere' }}>{blocks}</Box>;
};

const HelpChat: React.FC = () => {
  const t = useT();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { guides } = useScreenHelp();
  const [status, setStatus] = useState<ChatStatus | null>(null);
  useEffect(() => publishEnabled(!!status?.enabled), [status?.enabled]);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const nextId = useRef(1);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pending = useRef<AbortController | null>(null);

  // ¿Está activo el chat? Sin clave en el API (o un API sin el chat) no se muestra.
  useEffect(() => {
    let alive = true;
    axios
      .get<ChatStatus>('help/chat')
      .then(({ data }) => alive && setStatus(data?.enabled ? data : { enabled: false }))
      .catch(() => alive && setStatus({ enabled: false }));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  useEffect(() => () => pending.current?.abort(), []);

  // Siempre al último mensaje.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending, open]);

  useEffect(() => {
    if (open) window.setTimeout(() => inputRef.current?.focus(), 150);
  }, [open]);

  const maxLength = status?.maxQuestionLength || 500;

  const suggestions = useMemo(() => {
    const own = guides.slice(0, 2).map((g) => t('helpChat.suggestScreen', { title: g.title }));
    return [...own, t('helpChat.suggest1'), t('helpChat.suggest2'), t('helpChat.suggest3')].slice(0, 4);
  }, [guides, t]);

  const push = useCallback((m: Omit<ChatMessage, 'id'>) => {
    setMessages((prev) => [...prev, { ...m, id: nextId.current++ }]);
  }, []);

  const send = useCallback(
    async (raw: string) => {
      const question = raw.trim();
      if (!question || sending) return;
      if (question.length > maxLength) {
        push({ role: 'assistant', content: t('helpChat.tooLong', { n: question.length, max: maxLength }), error: 'warning' });
        return;
      }
      const history = messages
        .filter((m) => !m.error)
        .slice(-HISTORY_SENT)
        .map((m) => ({ role: m.role, content: m.content }));
      push({ role: 'user', content: question });
      setInput('');
      setSending(true);
      const controller = new AbortController();
      pending.current = controller;
      try {
        const { data } = await axios.post<{ answer: string; links: ChatLink[]; remaining: number | null }>(
          'help/ask',
          { question, lang: currentLang(), path: pathname, history },
          { signal: controller.signal },
        );
        push({ role: 'assistant', content: data.answer, links: Array.isArray(data.links) ? data.links : [] });
        setStatus((s) => (s ? { ...s, remaining: data.remaining ?? null } : s));
      } catch (error: any) {
        if (controller.signal.aborted) return;
        const code = error?.response?.data?.code;
        if (code === 'HELP_CHAT_DISABLED') {
          setOpen(false);
          setStatus({ enabled: false });
          return;
        }
        if (code === 'HELP_CHAT_USER_LIMIT' || code === 'HELP_CHAT_COMPANY_LIMIT') {
          setStatus((s) => (s ? { ...s, remaining: 0 } : s));
          push({ role: 'assistant', content: t(code === 'HELP_CHAT_USER_LIMIT' ? 'helpChat.userLimit' : 'helpChat.companyLimit'), error: 'warning' });
        } else if (code === 'HELP_CHAT_TOO_LONG') {
          push({ role: 'assistant', content: t('helpChat.tooLong', { n: question.length, max: maxLength }), error: 'warning' });
        } else {
          push({ role: 'assistant', content: t('helpChat.error'), error: 'error' });
        }
      } finally {
        if (pending.current === controller) pending.current = null;
        setSending(false);
      }
    },
    [sending, maxLength, messages, push, t, pathname],
  );

  const clear = () => {
    pending.current?.abort();
    pending.current = null;
    setSending(false);
    setMessages([]);
    setInput('');
    inputRef.current?.focus();
  };

  const go = (path: string) => {
    navigate(path);
    if (isPhone) setOpen(false);
  };

  if (!status?.enabled) return null;

  const tooLong = input.trim().length > maxLength;
  const remaining = status.remaining;
  const outOfQuestions = remaining === 0;

  return (
    <>
      {!open && (
        <Tooltip title={t('helpChat.ask')} placement="left">
          <Fab
            size="small"
            aria-label={t('helpChat.ask')}
            onClick={() => setOpen(true)}
            sx={{
              position: 'fixed',
              right: { xs: 12, sm: 20 },
              bottom: { xs: 'calc(12px + env(safe-area-inset-bottom))', sm: 20 },
              zIndex: (th) => th.zIndex.speedDial,
              bgcolor: 'background.paper',
              color: 'primary.main',
              border: 1,
              borderColor: 'divider',
              boxShadow: 3,
              '&:hover': { bgcolor: (th) => alpha(th.palette.primary.main, th.palette.mode === 'dark' ? 0.24 : 0.08) },
              '@media print': { display: 'none' },
            }}
          >
            <QuestionAnswerOutlined fontSize="small" />
          </Fab>
        </Tooltip>
      )}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        // Por encima de la barra superior, como el panel de ayuda de la pantalla.
        sx={{ zIndex: (th) => th.zIndex.drawer + 2 }}
        PaperProps={{
          role: 'dialog',
          'aria-labelledby': 'help-chat-title',
          sx: {
            width: { xs: '100%', sm: 420, xl: 480 },
            maxWidth: '100%',
            height: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            backgroundImage: 'none',
          },
        }}
      >
        {/* Encabezado */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ px: 2, py: 1.25, pt: 'calc(10px + env(safe-area-inset-top))', borderBottom: 1, borderColor: 'divider', flexShrink: 0, minWidth: 0 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography id="help-chat-title" variant="subtitle1" component="h2" sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
              {t('helpChat.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ lineHeight: 1.3 }}>
              {t('helpChat.subtitle')}
            </Typography>
          </Box>
          <Tooltip title={t('helpChat.clear')}>
            <span>
              <IconButton aria-label={t('helpChat.clear')} onClick={clear} disabled={!messages.length && !sending}>
                <DeleteSweepOutlined />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton aria-label={t('helpChat.close')} onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Conversación */}
        <Box
          ref={listRef}
          role="log"
          aria-live="polite"
          aria-relevant="additions"
          aria-busy={sending}
          sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}
        >
          {messages.length === 0 && (
            <Stack spacing={2} sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  aria-hidden
                  sx={{
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    color: 'primary.main',
                    bgcolor: (th) => alpha(th.palette.primary.main, th.palette.mode === 'dark' ? 0.2 : 0.1),
                  }}
                >
                  <QuestionAnswerOutlined fontSize="small" />
                </Box>
                <Typography variant="body2" sx={{ pt: 0.25 }}>
                  {t('helpChat.intro')}
                </Typography>
              </Stack>
              <Notice severity="info">{t('helpChat.privacy')}</Notice>
              <Box>
                <Typography variant="overline" color="text.secondary" component="p" sx={{ lineHeight: 1.6, mb: 1 }}>
                  {t('helpChat.suggestionsTitle')}
                </Typography>
                <Stack spacing={1}>
                  {suggestions.map((s) => (
                    <Button
                      key={s}
                      variant="outlined"
                      size="small"
                      onClick={() => void send(s)}
                      disabled={sending || outOfQuestions}
                      sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', fontWeight: 500, py: 0.75, whiteSpace: 'normal' }}
                    >
                      {s}
                    </Button>
                  ))}
                </Stack>
              </Box>
            </Stack>
          )}

          {messages.map((m) =>
            m.role === 'user' ? (
              <Box key={m.id} sx={{ alignSelf: 'flex-end', maxWidth: '85%', minWidth: 0 }}>
                <Typography component="span" sx={visuallyHidden}>
                  {t('helpChat.you')}:
                </Typography>
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    borderBottomRightRadius: 4,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    typography: 'body2',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {m.content}
                </Box>
              </Box>
            ) : m.error ? (
              <Box key={m.id} sx={{ minWidth: 0 }}>
                <Notice severity={m.error}>{m.content}</Notice>
              </Box>
            ) : (
              <Box key={m.id} sx={{ alignSelf: 'flex-start', maxWidth: '92%', minWidth: 0 }}>
                <Typography component="span" sx={visuallyHidden}>
                  {t('helpChat.assistant')}:
                </Typography>
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    borderBottomLeftRadius: 4,
                    bgcolor: (th) => (th.palette.mode === 'dark' ? alpha(th.palette.common.white, 0.06) : alpha(th.palette.text.primary, 0.05)),
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <AnswerText text={m.content} />
                  {m.links && m.links.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0.5 }}>
                        {t('helpChat.linksTitle')}
                      </Typography>
                      <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
                        {m.links.map((l) => (
                          <Button
                            key={l.path}
                            size="small"
                            variant="outlined"
                            endIcon={<ArrowForwardIcon fontSize="small" />}
                            onClick={() => go(l.path)}
                            sx={{ textTransform: 'none', maxWidth: '100%', whiteSpace: 'normal', textAlign: 'left', bgcolor: 'background.paper' }}
                          >
                            {l.label}
                          </Button>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Box>
            ),
          )}

          {sending && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: 'flex-start', color: 'text.secondary' }} role="status">
              <Stack direction="row" spacing={0.5} aria-hidden>
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: 'text.secondary',
                      animation: `${blink} 1.2s ${i * 0.15}s infinite ease-in-out`,
                      '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0.6 },
                    }}
                  />
                ))}
              </Stack>
              <Typography variant="caption">{t('helpChat.typing')}</Typography>
            </Stack>
          )}
        </Box>

        {/* Pregunta */}
        <Box
          component="form"
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            void send(input);
          }}
          sx={{ px: 2, pt: 1.5, pb: 'calc(12px + env(safe-area-inset-bottom))', borderTop: 1, borderColor: 'divider', flexShrink: 0 }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              inputRef={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !(e.nativeEvent as KeyboardEvent).isComposing) {
                  e.preventDefault();
                  void send(input);
                }
              }}
              placeholder={t('helpChat.placeholder')}
              inputProps={{ 'aria-label': t('helpChat.inputLabel'), maxLength: maxLength + 200 }}
              error={tooLong}
              disabled={outOfQuestions}
              multiline
              maxRows={4}
              size="small"
              fullWidth
            />
            <Tooltip title={t('helpChat.send')} disableFocusListener>
              <span>
                <IconButton
                  type="submit"
                  color="primary"
                  aria-label={t('helpChat.send')}
                  disabled={!input.trim() || sending || tooLong || outOfQuestions}
                  sx={{ mb: 0.25 }}
                >
                  <SendIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mt: 0.75, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0, display: { xs: 'none', sm: 'block' } }}>
              {t('helpChat.enterHint')}
            </Typography>
            <Typography variant="caption" color={tooLong ? 'error' : 'text.secondary'} sx={{ ml: 'auto', flexShrink: 0 }}>
              {input.length > maxLength * 0.8
                ? `${input.trim().length}/${maxLength}`
                : typeof remaining === 'number'
                  ? t('helpChat.remaining', { n: remaining })
                  : ''}
            </Typography>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
};

const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

export default HelpChat;
